import { EntityResourceChanged, NO_RESOURCE_ID } from '../../../store/actions/entity-resource-actions';
import { tap, filter, switchMap, take, map, catchError } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { getCurrentUser, getCurrentUserLoadedStatus } from '../../../store/selectors/current-user-selectors';
import { Auth } from 'Src/ng2/shared/auth/auth.service';
import { LoadCurrentUser } from 'Src/ng2/store';
import { IUser } from 'Src/ng2/shared/typings/interfaces/user.interface';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { ENTITY_TYPE } from 'Src/ng2/shared/typings/types/entity-type.types';
import { UrlNavigationService } from 'Src/ng2/shared/services/url-navigation/url-navigation.service';
import { resolveNetworkClusterContext } from '../../network-cluster-context';
import { StoreDataService } from 'Src/ng2/shared/services/store-data-service/store-data.service';
import { DeferrableLoad } from '../../deferred-route-loads';

@Injectable({
  providedIn: 'root',
})
// Announces the One Portal resource a navigation is for — a single entity or a network, on either
// route tree — so the store reset lands before the toggle and access guards read it, and loads the
// school document the rest of the guard chain reads.
export class CurrentEntityGuard implements CanActivate {
  // School ids only. They are the one id this guard puts in a request URL, and the pattern was
  // written for their shape; shelter and cluster ids are announced, never validated here.
  private SCHOOL_ID_REGEX = /^[0-9a-zA-Z]+$/;

  constructor (
    private store: Store<any>,
    private router: Router,
    private auth: Auth,
    private ImUser: ImUser,
    private storeData: StoreDataService,
  ) {}

  canActivate (routeSnapshot: ActivatedRouteSnapshot): Observable<UrlTree | boolean> {
    const { entityId, entityType } = UrlNavigationService.getOpContext(routeSnapshot);
    // Networks declare their id as a param on a child of the route this guard runs from, so it
    // takes a walk rather than a read. Covers both trees: the OP matcher sets clusterId on its own
    // snapshot, the legacy `network` route on the cluster child.
    const { networkId } = resolveNetworkClusterContext(routeSnapshot);
    const resourceId = entityId || networkId;

    // The school document and the CUNY / ExpandEd redirect belong to a single school. An entity id
    // is what distinguishes one: a network carries none, and the legacy `:schoolId` tree supplies
    // it through getEntityId while carrying no entityType.
    const isSingleSchool = !!entityId && (!entityType || entityType === ENTITY_TYPE.SCHOOL);

    // Checked before announcing, so a malformed id does not reset the store on its way to
    // not-found.
    if (isSingleSchool && !this.SCHOOL_ID_REGEX.test(entityId)) return of(this.router.createUrlTree(['/not-found']));

    // Ahead of every fetch: the reset keys on this and has to land before TogglesGuard and the
    // evaluation guards, so none of them evaluates against the outgoing resource. A route naming
    // no resource — `multi/all-entities` — announces the sentinel rather than staying silent, so
    // that entering it resets the outgoing entity's slices. Staying on it re-announces the same
    // sentinel, which the meta-reducer reads as no change.
    this.store.dispatch(new EntityResourceChanged({ resourceId: resourceId || NO_RESOURCE_ID }));

    if (!isSingleSchool) return of(true);

    // Started, never awaited: this guard does not re-run on an in-entity navigation, so it cannot
    // decide for the page being entered. SchoolReadyGuard does that.
    this.storeData.startLoad(DeferrableLoad.School, entityId);

    return this.checkSpecialUser();
  }

  // CUNY and ExpandEd users have no single-school view; the check reads the user, not the school.
  private checkSpecialUser (): Observable<UrlTree | boolean> {
    return this.getCurrentUserFromStoreOrAPI().pipe(
      map((currentUser: IUser) => {
        const isCUNYUser = this.ImUser.isCUNY(currentUser);
        const isExpandEdUser = this.ImUser.isExpandEd(currentUser);
        if (isCUNYUser || isExpandEdUser) return this.router.createUrlTree(['/not-found']);

        return true;
      }),
      catchError(() => of(false)),
    );
  }

  canActivateChild (routeSnapshot: ActivatedRouteSnapshot): Observable<UrlTree | boolean> {
    return this.canActivate(routeSnapshot);
  }

  getCurrentUserFromStoreOrAPI (): Observable<IUser> {
    return this.store.pipe(
      select(getCurrentUserLoadedStatus),
      tap(async loaded => {
        if (!loaded) {
          const email = await this.auth.getCurrentUserEmail();
          this.store.dispatch(new LoadCurrentUser({ email }));
        }
      }),
      filter(loaded => loaded),
      switchMap(() => this.store.pipe(select(getCurrentUser))),
      take(1),
    );
  }
}
